/**
 * Stones, the cairn and the fall, ported from `CairnDesign` on the phone.
 *
 * Every number here is the phone's: the outlines, the glints, the stacking
 * ladder and the drop curve. A task keeps the same outline on both clients
 * because the outline is chosen from its id with the same hash, so the stone
 * that lands on the web's cairn is the stone the phone would have placed.
 */

import type { Task } from '../../shared/model';

/** The seven semantic tones, as stones. The colours are the same in both themes. */
export type Mineral = 'amethyst' | 'jade' | 'sandstone' | 'lapis' | 'amber' | 'granite' | 'garnet';

export type Tone = 'neutral' | 'priority' | 'info' | 'focus' | 'success' | 'attention' | 'danger';

export const mineralTone: Record<Mineral, Tone> = {
  amethyst: 'focus',
  jade: 'success',
  sandstone: 'attention',
  lapis: 'info',
  amber: 'priority',
  granite: 'neutral',
  garnet: 'danger',
};

/**
 * Which stone a task becomes: the colour of where it lives, so a finished day
 * shows where it went. Filing wins over the person, because a project task
 * someone is waiting on is still project work.
 */
export function taskMineral(
  task: Pick<Task, 'dropped' | 'projectId' | 'areaId' | 'personId'>,
): Mineral {
  if (task.dropped) {
    return 'granite';
  }

  if (task.projectId) {
    return 'amethyst';
  }

  if (task.areaId) {
    return 'jade';
  }

  if (task.personId) {
    return 'sandstone';
  }

  return 'lapis';
}

/** Five outlines for tasks and `f`, the flat capstone that only tops a finished day. */
export type Silhouette = 'a' | 'b' | 'c' | 'd' | 'e' | 'f';

export const taskSilhouettes: Silhouette[] = ['a', 'b', 'c', 'd', 'e'];

type Point = [number, number];

/** Start point, then four cubic segments of (control, control, end), in a 100 × 64 box. */
const outlines: Record<Silhouette, { start: Point; curves: [Point, Point, Point][] }> = {
  a: {
    start: [7, 40],
    curves: [
      [
        [5, 26],
        [21, 12],
        [46, 11],
      ],
      [
        [71, 10],
        [94, 18],
        [96, 33],
      ],
      [
        [98, 48],
        [81, 59],
        [54, 59.5],
      ],
      [
        [28, 60],
        [9, 53],
        [7, 40],
      ],
    ],
  },
  b: {
    start: [10, 35],
    curves: [
      [
        [9, 19],
        [27, 7.5],
        [50, 7.5],
      ],
      [
        [74, 7.5],
        [91, 19],
        [90.5, 35],
      ],
      [
        [90, 51],
        [72, 59.5],
        [49, 59],
      ],
      [
        [27, 58.5],
        [11, 50],
        [10, 35],
      ],
    ],
  },
  c: {
    start: [5, 33],
    curves: [
      [
        [6, 22],
        [25, 15.5],
        [50, 15],
      ],
      [
        [77, 14.5],
        [96, 21],
        [96, 32.5],
      ],
      [
        [96, 44],
        [78, 50.5],
        [51, 50.5],
      ],
      [
        [24, 50.5],
        [4, 44],
        [5, 33],
      ],
    ],
  },
  d: {
    start: [12, 39],
    curves: [
      [
        [8, 25],
        [22, 10.5],
        [43, 10],
      ],
      [
        [65, 9.5],
        [90, 14],
        [94, 31],
      ],
      [
        [98, 47],
        [81, 57.5],
        [57, 57.5],
      ],
      [
        [33, 57.5],
        [15, 52],
        [12, 39],
      ],
    ],
  },
  e: {
    start: [14, 35],
    curves: [
      [
        [13, 20],
        [29, 10],
        [50, 10],
      ],
      [
        [71, 10],
        [87, 20],
        [86, 35],
      ],
      [
        [85, 50],
        [69, 58],
        [49, 57.5],
      ],
      [
        [30, 57],
        [15, 49],
        [14, 35],
      ],
    ],
  },
  f: {
    start: [8, 36.5],
    curves: [
      [
        [9, 27],
        [27, 21],
        [50, 21],
      ],
      [
        [74, 21],
        [92, 27],
        [92, 36.5],
      ],
      [
        [92, 45.5],
        [74, 50.5],
        [50, 50.5],
      ],
      [
        [27, 50.5],
        [7, 45.5],
        [8, 36.5],
      ],
    ],
  },
};

/** The outline as SVG path data in its 100 × 64 box. */
export function silhouettePath(silhouette: Silhouette) {
  const { start, curves } = outlines[silhouette];

  return `M${start.join(' ')} ${curves.map(([a, b, c]) => `C${a.join(' ')} ${b.join(' ')} ${c.join(' ')}`).join(' ')} Z`;
}

/** Where the drawn stone begins and ends vertically in the 64-high box. */
export const visibleTop: Record<Silhouette, number> = { a: 11, b: 7.5, c: 15, d: 10, e: 10, f: 21 };

export const visibleBottom: Record<Silhouette, number> = {
  a: 59.6,
  b: 59.3,
  c: 50.5,
  d: 57.5,
  e: 57.7,
  f: 50.5,
};

/** The white glint: centre and radii. */
export const specular: Record<Silhouette, { cx: number; cy: number; rx: number; ry: number }> = {
  a: { cx: 36, cy: 21, rx: 15, ry: 5 },
  b: { cx: 35, cy: 18, rx: 13, ry: 5 },
  c: { cx: 34, cy: 23, rx: 16, ry: 4 },
  d: { cx: 33, cy: 20, rx: 13, ry: 5 },
  e: { cx: 36, cy: 20, rx: 12, ry: 4.5 },
  f: { cx: 36, cy: 28, rx: 14, ry: 3.5 },
};

/** A few darker grains, so a stone reads as mineral rather than plastic. */
export const speckles: Record<Silhouette, [number, number, number][]> = {
  a: [
    [62, 43, 1.6],
    [72, 30, 1.1],
    [44, 50, 1.3],
  ],
  b: [
    [60, 45, 1.4],
    [69, 28, 1],
    [38, 48, 1.2],
  ],
  c: [
    [64, 39, 1.3],
    [75, 28, 1],
    [40, 43, 1.1],
  ],
  d: [
    [66, 41, 1.5],
    [58, 26, 1],
    [38, 47, 1.2],
  ],
  e: [
    [60, 44, 1.3],
    [66, 29, 1],
  ],
  f: [[62, 42, 1.1]],
};

/** The width-to-height ratio of a stone's box. Only a very tall cairn draws its stones flatter. */
export const stoneAspect = 100 / 64;

const encoder = new TextEncoder();

/**
 * A stable outline for an id: FNV-1a over its UTF-8 bytes, exactly as the
 * phone computes it, so a task has one shape on every device.
 */
export function seededSilhouette(id: string): Silhouette {
  let hash = 2166136261;

  for (const byte of encoder.encode(id)) {
    hash ^= byte;
    hash = Math.imul(hash, 16777619) >>> 0;
  }

  return taskSilhouettes[hash % taskSilhouettes.length];
}

export type CairnStone = { id: string; mineral: Mineral; silhouette: Silhouette };

export function taskStone(
  id: string,
  task: Pick<Task, 'dropped' | 'projectId' | 'areaId' | 'personId'>,
): CairnStone {
  return { id, mineral: taskMineral(task), silhouette: seededSilhouette(id) };
}

/**
 * Widths from the base up, at scale 1. There is no cap on stones: a stack
 * with more stones than the ladder has steps spreads the same taper over all
 * of them, so no stone shrinks to a sliver.
 */
export const ladder = [76, 60, 48, 38, 30, 24, 20, 17];

/** The tallest a stack stands at scale 1. Past it, stones get smaller rather than the stack taller. */
export const tallest = 140;

const capstoneWidening = 1.12;
const nesting = 0.17;
const rotations = [-2, 3, -4, 2.5, -3, 2, -2, 1];
const nudges = [0, 3, -2, 2, -1, 1, 0, 1];
/** How far the top of a very tall stack stands to the side of its base, and turns. */
const leanLimit = 30;
const turnLimit = 8;

export type Slot = {
  silhouette: Silhouette;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Degrees, clockwise. */
  rotation: number;
};

/**
 * How far the top of a stack of `tasks` stones leans, at scale 1. Past five it
 * starts to lean, the honest picture of a day holding more than it can, and
 * it eases towards `leanLimit` so the stack bows and never topples.
 */
export function lean(tasks: number) {
  return tasks > 5 ? leanLimit * (1 - Math.exp(-(tasks - 5) / 3)) : 0;
}

/** The step for the stone at `index` in a stack with room for `positions`. */
function widthAt(index: number, positions: number) {
  const last = ladder.length - 1;

  if (positions <= ladder.length) {
    return ladder[Math.min(index, last)];
  }

  const step = (index * last) / (positions - 1),
    below = Math.floor(step);

  if (below >= last) {
    return ladder[last];
  }

  return ladder[below] * (ladder[below + 1] / ladder[below]) ** (step - below);
}

/** Every stack keeps room for its capstone, so the capstone landing moves nothing below it. */
function withCapstone(silhouettes: Silhouette[]): Silhouette[] {
  return silhouettes.at(-1) === 'f' ? silhouettes : [...silhouettes, 'f'];
}

function place(
  silhouettes: Silhouette[],
  positions: number,
  centerX: number,
  ground: number,
  across: number,
  up: number,
): Slot[] {
  const slots: Slot[] = [];
  const leaning = lean(positions - 1);
  let bottom = ground + 1.5 * up;

  silhouettes.forEach((silhouette, index) => {
    const step = widthAt(index, positions) * (silhouette === 'f' ? capstoneWidening : 1);
    const width = step * across;
    const height = (step * up) / stoneAspect;
    const top = bottom - (visibleBottom[silhouette] / 64) * height;
    const along = positions > 1 ? index / (positions - 1) : 0;
    const nudge = nudges[index % nudges.length] * Math.min(1, widthAt(index, positions) / 24);
    const center = centerX + (nudge + leaning * along ** 1.6) * across;
    const turn = rotations[index % rotations.length] + (leaning / leanLimit) * turnLimit * along;

    slots.push({ silhouette, x: center - width / 2, y: top, width, height, rotation: turn });

    const visible = top + (visibleTop[silhouette] / 64) * height;
    const next = (widthAt(index + 1, positions) * up) / stoneAspect;

    bottom = visible + nesting * next;
  });

  return slots;
}

/**
 * How much a stack is scaled across and up so it stands no taller than
 * `tallest`. It is flattened more than it is narrowed, so a tall stack keeps a
 * cairn's footprint instead of thinning to a spire.
 */
export function cairnFit(silhouettes: Silhouette[], scale = 1) {
  const full = withCapstone(silhouettes);
  const natural = -Math.min(...place(full, full.length, 0, 0, 1, 1).map(s => s.y));
  const up = natural > tallest ? tallest / natural : 1;

  return { across: Math.sqrt(up) * scale, up: up * scale };
}

/** The slots for a stack, from the base up, standing on `ground` and centred on `centerX`. */
export function cairnSlots(
  silhouettes: Silhouette[],
  centerX: number,
  ground: number,
  scale = 1,
): Slot[] {
  const { across, up } = cairnFit(silhouettes, scale);

  return place(silhouettes, withCapstone(silhouettes).length, centerX, ground, across, up);
}

/**
 * The smallest box a stack of `count` stones fits in, with room above for
 * sparkles. It is as wide on the left of the base as on the right, so a
 * leaning stack still stands where its box is placed.
 */
export function cairnExtent(count: number, scale = 1) {
  const silhouettes = Array.from({ length: Math.max(count, 1) }, (_, i) =>
    i === count - 1 ? 'f' : 'b',
  ) as Silhouette[];

  const slots = cairnSlots(silhouettes, 0, 0, scale);
  const top = Math.min(...slots.map(s => s.y));
  const reach = Math.max((ladder[0] * scale) / 2, ...slots.map(s => Math.max(-s.x, s.x + s.width)));

  return { width: 2 * reach + 24 * scale, height: -top + 36 * scale };
}

/**
 * The path of a falling stone: 150 points with a spin, a landing, one bounce
 * and a settle, as plain arithmetic so a stone can only ever end at rest.
 */
export const drop = {
  duration: 950,
  /** Milliseconds to the first touch and to the second, smaller one. */
  landing: 0.52 * 950,
  settling: 0.82 * 950,
};

export function dropOffset(t: number) {
  if (t >= 1) {
    return 0;
  }

  const p = Math.max(t, 0);

  if (p < 0.52) {
    const k = p / 0.52;

    return -150 * (1 - k * k);
  }

  if (p < 0.68) {
    const k = (p - 0.52) / 0.16;

    return -12 * (1 - (1 - k) * (1 - k));
  }

  if (p < 0.82) {
    const k = (p - 0.68) / 0.14;

    return -12 * (1 - k * k);
  }

  if (p < 0.91) {
    const k = (p - 0.82) / 0.09;

    return -2 * (1 - (1 - k) * (1 - k));
  }

  const k = (p - 0.91) / 0.09;

  return -2 * (1 - k * k);
}

const spinPoints: Point[] = [
  [0, -14],
  [0.52, 4],
  [0.68, -2],
  [0.82, 1],
  [0.91, 0],
  [1, 0],
];

export function dropSpin(t: number) {
  const p = Math.min(Math.max(t, 0), 1);

  for (let i = 1; i < spinPoints.length; i++) {
    if (p <= spinPoints[i][0]) {
      const [t0, v0] = spinPoints[i - 1],
        [t1, v1] = spinPoints[i];

      return v0 + ((v1 - v0) * (p - t0)) / (t1 - t0);
    }
  }

  return 0;
}

/**
 * The fall as Web Animations keyframes, sampled finely and played linearly,
 * so the gravity and the bounce live in the curve rather than in an easing.
 */
export function dropKeyframes(rotation: number): Keyframe[] {
  return Array.from({ length: 61 }, (_, i) => {
    const t = i / 60;

    return {
      offset: t,
      opacity: Math.min(t / 0.1, 1),
      transform: `translateY(${dropOffset(t).toFixed(2)}px) rotate(${(rotation + dropSpin(t)).toFixed(2)}deg)`,
    };
  });
}
